import React from 'react';
import Container from '@grnhse/seedling/lib/birch/components/container';
import { Person } from 'talent_matching/types';
import Collapsible from '@grnhse/seedling/lib/birch/components/collapsible';
import {
  Body,
  SecondaryBody,
  Label,
  SectionTitle,
} from '@grnhse/seedling/lib/birch/typography';
import { Row } from 'shared/components/birch/row';
import { withLocalePrefix } from 'shared/utils/translation';
import styled from 'styled-components';
import ArrowUpIcon from '@grnhse/seedling/lib/birch/icons/arrow_up';
import ArrowDownIcon from '@grnhse/seedling/lib/birch/icons/arrow_down';
import StrengthIndicator from 'talent_matching/components/shared/strength_indicator';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import AiScoringFeedback, {
  getEnFeedbackMap,
} from 'shared/components/ai_feedback';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import AiMagicIcon from '@grnhse/seedling/lib/birch/icons/ai_magic';
import { WarningTag } from '@grnhse/seedling/lib/birch/components/tags';
import { useLocalStorage } from 'react-use';

const t = withLocalePrefix('talent_matching');

type MatchStrengthExplanationProps = {
  application: Partial<Person>;
  openCalibrationModal?: () => void;
  openOverrideModal: () => void;
  showExplanationKey: string;
  hasCalibrationSetting?: boolean;
};

type ExplanationRow = {
  key: string;
  label: string;
  value: string;
  metadata: boolean;
};

const ButtonWrapper = styled.div`
  button {
    padding-left: 0;
  }
`;

const ClickableHeader = styled.div`
  cursor: pointer;
`;

const ExplanationList = styled(Stack).attrs({ as: 'dl' })`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: ${Tokens.spacing['2']};
  row-gap: ${Tokens.spacing['1']};
  align-items: baseline;
  margin: 0;
`;

const ExplanationLabel = styled(SecondaryBody).attrs({ as: 'dt' })`
  white-space: nowrap;
  font-weight: 600;
`;

const ExplanationValue = styled(SecondaryBody).attrs({ as: 'dd' })`
  margin: 0;
`;

const MatchStrengthExplanation = ({
  application,
  openCalibrationModal,
  openOverrideModal,
  showExplanationKey,
  hasCalibrationSetting = true,
}: MatchStrengthExplanationProps) => {
  const [showExplanation, setShowExplanation] = useLocalStorage<boolean>(
    showExplanationKey,
    true
  );

  const isOverride = !!application.overrideMatchStrength;
  const enUserFeedback = getEnFeedbackMap('application_ai_score_feedback');

  const matchStrength = application.matchStrength;
  const explanation = application.matchStrengthExplanation;

  const overrideCtaKey = !matchStrength
    ? 'assign_match_score'
    : 'override_match_score';

  const renderExplanation = () => {
    if (isOverride) {
      return (
        <Stack spacing={2}>
          <Stack spacing={0.5}>
            <Label>{t('match_strength_explanation.override_note')}</Label>
            <SecondaryBody className="pii-blur" metadata>
              {application?.overrideNote || '--'}
            </SecondaryBody>
          </Stack>
          <Stack spacing={0.5}>
            <Label>{t('match_strength_explanation.override_reason')}</Label>
            <SecondaryBody className="pii-blur" metadata>
              {application.overrideReason || '--'}
            </SecondaryBody>
          </Stack>
        </Stack>
      );
    }

    if (!matchStrength || matchStrength === 'unknown') {
      return (
        <SecondaryBody>
          {t('match_strength_explanation.no_match_strength')}
        </SecondaryBody>
      );
    }

    if (explanation?.no_matched_criteria) {
      return (
        <SecondaryBody>
          {t('match_strength_explanation.no_matched_criteria')}
        </SecondaryBody>
      );
    }

    const explanationRows: ExplanationRow[] = [
      {
        key: 'experience',
        label: t('match_strength_explanation.labels.experience'),
        value:
          explanation?.experience_explanation ??
          t('match_strength_explanation.job_titles_not_set'),
        metadata: !explanation?.experience_explanation,
      },
      {
        key: 'industries',
        label: t('match_strength_explanation.labels.industries'),
        value:
          explanation?.industry_explanation ??
          t('match_strength_explanation.industries_not_set'),
        metadata: !explanation?.industry_explanation,
      },
      {
        key: 'skills',
        label: t('match_strength_explanation.labels.skills'),
        value:
          explanation?.skills_explanation ??
          t('match_strength_explanation.skills_not_set'),
        metadata: !explanation?.skills_explanation,
      },
    ];

    return (
      <Stack spacing={2}>
        <SecondaryBody>
          {t('match_strength_explanation.based_on_calibration')}
        </SecondaryBody>
        <ExplanationList spacing={0}>
          {explanationRows.map(({ key, label, value, metadata }) => (
            <React.Fragment key={key}>
              <ExplanationLabel>{label}</ExplanationLabel>
              <ExplanationValue
                className={metadata ? undefined : 'pii-blur'}
                metadata={metadata}
              >
                {value}
              </ExplanationValue>
            </React.Fragment>
          ))}
        </ExplanationList>
      </Stack>
    );
  };

  const renderHeader = () => {
    return (
      <Row justifyContent="space-between">
        {!hasCalibrationSetting || !matchStrength ? (
          <SectionTitle>{t('match_strengths.default')}</SectionTitle>
        ) : (
          <Row spacing={1} alignItems={'center'}>
            <SectionTitle>{t(`match_strengths.${matchStrength}`)}</SectionTitle>
            {matchStrength !== 'unknown' && (
              <StrengthIndicator matchStrength={matchStrength} />
            )}
            {isOverride && <WarningTag text={t('override')} />}
          </Row>
        )}
        {showExplanation ? (
          <ArrowUpIcon size="sm" />
        ) : (
          <ArrowDownIcon size="sm" />
        )}
      </Row>
    );
  };

  const renderContent = () => {
    if (!hasCalibrationSetting && openCalibrationModal) {
      return (
        <Stack spacing={0} sx={{ padding: 0, paddingTop: 2 }}>
          <SecondaryBody metadata>
            {t('match_strength_explanation.no_calibration_setting')}
          </SecondaryBody>
          <ButtonWrapper>
            <TertiaryButton
              onClick={openCalibrationModal}
              size="md"
              icon={AiMagicIcon}
            >
              {t('match_strength_explanation.calibrate')}
            </TertiaryButton>
          </ButtonWrapper>
        </Stack>
      );
    }

    return (
      <Stack spacing={2} sx={{ padding: 0, paddingTop: 2 }}>
        {renderExplanation()}
        <Stack spacing={1} direction="column">
          <ButtonWrapper>
            <TertiaryButton onClick={openOverrideModal} size="md">
              {t(overrideCtaKey)}
            </TertiaryButton>
          </ButtonWrapper>
          {matchStrength && matchStrength !== 'unknown' && !isOverride && (
            <Stack sx={{ alignSelf: 'flex-start' }} flexWrap="wrap">
              <AiScoringFeedback
                featureKey="application_ai_score_feedback"
                enUserFeedback={enUserFeedback}
                payload={{
                  application_id: application.id,
                  hiring_plan_id: application.hiringPlanId,
                }}
                showComments
                labelMediumFont={false}
                key={application.id}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    );
  };

  return (
    <Container>
      <Stack sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }} spacing={0}>
        <ClickableHeader onClick={() => setShowExplanation(!showExplanation)}>
          {renderHeader()}
        </ClickableHeader>
        <Collapsible id="match-strength-explanation" isOpen={!!showExplanation}>
          {renderContent()}
        </Collapsible>
      </Stack>
    </Container>
  );
};

export default MatchStrengthExplanation;
